// @file Centralized Pinia store for audio device state (device list, active selection, caching, devicechange listener).

import type { MediaDevice } from '@@/bits/media_devices'
import { safeLocalStorage } from '@@/bits/safe_storage'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const ACTIVE_AUDIO_DEVICE_NAME_CACHE_KEY = 'activeAudioDeviceName'
const NO_MIC_CACHE_VALUE = 'no microphone'

function enumerateAudioInputDevices(): Promise<MediaDevice[]> {
  return navigator.mediaDevices
    .enumerateDevices()
    .then((devices) =>
      devices.filter((d) => d.kind === 'audioinput').map((d): MediaDevice => ({ id: d.deviceId, label: d.label })),
    )
}

/**
 * Prompts for microphone permission (required by Firefox to avoid
 * OverconstrainedError) then enumerates available audio input devices.
 */
async function fetchAudioInputDevices(): Promise<MediaDevice[]> {
  const tempStream = await navigator.mediaDevices.getUserMedia({ audio: true })
  tempStream.getTracks().forEach((track) => track.stop())
  return enumerateAudioInputDevices()
}

/**
 * Resolves the audio device ID from the localStorage cache.
 *
 * @returns
 *   - `undefined` when there is no cached value (caller should decide on a default)
 *   - `null` when the user explicitly chose "no microphone"
 *   - `string` device ID when a cached device was found, or `defaultDeviceId` as fallback
 */
function resolveAudioDeviceFromCache(
  audioDevices: MediaDevice[],
  defaultDeviceId: string | null | undefined,
): string | null | undefined {
  const cachedLabel = safeLocalStorage.getItem(ACTIVE_AUDIO_DEVICE_NAME_CACHE_KEY)
  if (cachedLabel == null) return undefined
  if (cachedLabel === NO_MIC_CACHE_VALUE) return null
  const cachedDevice = audioDevices.find((d) => d.label === cachedLabel)
  return cachedDevice?.id ?? defaultDeviceId ?? null
}

const useAudioDevicesStore = defineStore('audioDevices', () => {
  // #region State
  const audioDevices = ref<MediaDevice[]>([])
  const activeAudioDeviceId = ref<string | null>(null)

  let loaded = false
  let loadPromise: Promise<void> | null = null
  // #endregion

  // #region Computeds
  const activeAudioDevice = computed<MediaDevice | null>(() =>
    activeAudioDeviceId.value != null
      ? (audioDevices.value.find((d) => d.id === activeAudioDeviceId.value) ?? null)
      : null,
  )

  const audioConstraints = computed<MediaStreamConstraints>(() => ({
    audio: {
      deviceId: activeAudioDeviceId.value != null ? { exact: activeAudioDeviceId.value } : undefined,
    },
  }))
  // #endregion

  // #region Actions
  function selectAudioDevice(device: MediaDevice | null): void {
    activeAudioDeviceId.value = device?.id ?? null
    const label = device?.label
    safeLocalStorage.setItem(ACTIVE_AUDIO_DEVICE_NAME_CACHE_KEY, label != null ? label : NO_MIC_CACHE_VALUE)
  }

  async function ensureAudioDevicesLoaded(): Promise<void> {
    if (loaded) return
    if (loadPromise != null) return loadPromise

    loadPromise = (async () => {
      try {
        const devices = await fetchAudioInputDevices()
        audioDevices.value = devices

        const firstDeviceId = devices[0]?.id
        const deviceIdFromCache = resolveAudioDeviceFromCache(devices, firstDeviceId)
        const isNoValueCached = deviceIdFromCache === undefined
        activeAudioDeviceId.value = isNoValueCached ? (firstDeviceId ?? null) : deviceIdFromCache

        loaded = true
      } finally {
        loadPromise = null
      }
    })()

    return loadPromise
  }

  function switchToFirstAudioDevice(): void {
    activeAudioDeviceId.value = audioDevices.value[0]?.id ?? null
  }

  async function refreshAudioDevices(): Promise<void> {
    if (!loaded) return

    const devices = await enumerateAudioInputDevices()
    audioDevices.value = devices

    if (activeAudioDeviceId.value != null && !devices.some((d) => d.id === activeAudioDeviceId.value)) {
      switchToFirstAudioDevice()
    }
  }
  // #endregion

  // #region devicechange listener
  if (typeof navigator !== 'undefined' && navigator.mediaDevices != null) {
    navigator.mediaDevices.addEventListener('devicechange', () => {
      void refreshAudioDevices()
    })
  }
  // #endregion

  return {
    audioDevices,
    activeAudioDeviceId,
    activeAudioDevice,
    audioConstraints,
    selectAudioDevice,
    ensureAudioDevicesLoaded,
    switchToFirstAudioDevice,
    refreshAudioDevices,
  }
})

export { useAudioDevicesStore }
